import React, { Component } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import SweetAlert from 'react-bootstrap-sweetalert';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import "@ag-grid-enterprise/all-modules/dist/styles/ag-theme-balham.css";

var redirectURL = require('../redirectURL');

// ---------------------------------------------------------------------------------------------
// ACTUAL SOB CONFIGURATION - a pop-up on the Actual SOB screen (client, 2026-10-14), opened from
// the Configuration button beside its title. Not a menu screen of its own: it is the setup behind
// the numbers on that screen.
//
// WHAT IT SOLVES. The MSIL feed reports each dispatch with an MSIL PLANT code (the consignor) and
// a TRANSPORTER code - and MSIL's transporter codes are CHILD codes, one per plant/contract, not
// the transporter code our masters use. For most warehouses the plant code alone is enough: the
// Loading Ports master already records each warehouse's MSIL plant short name, so the whole
// consignor maps to one warehouse and nothing has to be configured here.
//
// Four warehouses share ONE MSIL plant (MSILG covers GUR regular, GUR hub, RPDCGUR and DELHI NCR;
// MSILDM covers Manesar), so for those the transporter child code is the only thing that says
// which warehouse earned the volume. Those code lists used to be hardcoded in the engine; this
// screen holds them instead.
//
// WAREHOUSE SCOPED: the rules shown are the ones that credit the warehouse selected in the page
// header, and a new rule is created for that warehouse.
//
// Props: open, onClose(), loadingPortCode (the selected warehouse), readOnly (the host screen's
// page access - the pop-up never resolves access of its own, so it cannot grant more).
// ---------------------------------------------------------------------------------------------

var GRID_DEFAULTS = { sortable: true, filter: true, resizable: true };

// "ⓘ" in a header marks a column that explains itself on hover (headerTooltip).
var INFO = " ⓘ";

var STOCK_YARD_RULES = [
    { value: "exclude", label: "Exclude stock yard dispatches" },
    { value: "include", label: "Only stock yard dispatches" },
    { value: "ignore", label: "Count both" }
];

// What the credited volume counts against. Hub is a FLAG on the row (its own share of business),
// Dedicated is a planning type (data_type 3) - both are one choice as far as the user is
// concerned, so they are offered as one list and split on save.
// `master` is which tab of the Transporters master holds this load type's MSIL child codes - the
// codes offered for a rule come from there, so the two must line up. A hub contract is reported
// under different child codes from the same transporter's regular one, which is exactly why the
// master keeps one list per tab rather than one per transporter.
var LOAD_TYPES = [
    { value: "regular", label: "Regular", is_hub: 0, data_type: 0,
      master: { data_type: 1, hub_transporter: 0 } },
    { value: "hub", label: "Hub", is_hub: 1, data_type: 0,
      master: { data_type: 1, hub_transporter: 1 } },
    { value: "dedicated", label: "Dedicated", is_hub: 0, data_type: 3,
      master: { data_type: 3, hub_transporter: 0 } }
];

function loadTypeOf(row) {
    if (row && (row.is_hub == 1)) { return "hub"; }
    if (row && parseInt(row.data_type) === 3) { return "dedicated"; }
    return "regular";
}

function loadTypeLabel(row) {
    var key = loadTypeOf(row);
    var hit = LOAD_TYPES.filter(function (t) { return t.value === key; });
    return hit.length > 0 ? hit[0].label : "Regular";
}

var ALL_CODES = "ALL";

function ruleLabel(value) {
    var hit = STOCK_YARD_RULES.filter(function (r) { return r.value === value; });
    return hit.length > 0 ? hit[0].label : "Count both";
}

// Accepts a typed code or a pasted list (comma / newline / semicolon / space separated), keeps
// what is already there, upper-cases and de-duplicates.
function mergeCodes(current, input) {
    var out = (current || []).slice();
    String(input || "").split(/[,;\s]+/).forEach(function (raw) {
        var code = String(raw || "").trim().toUpperCase();
        if (code && out.indexOf(code) < 0) { out.push(code); }
    });
    return out;
}

function codesToText(codes) {
    return (codes || []).join(", ");
}

// Users work in warehouse NAMES. The Optirun code (GUR, GUR-2, RPDCGUR...) is what gets stored,
// but it is never shown - the name comes from the Loading Ports master, which is also what the
// page header's warehouse selector shows.
function warehouseLabel(code) {
    return code === "GUR-2" ? "DELHI NCR" : (code || "");
}

export default class ActualSobConfigPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadshow: 'show-n',
            slices: [],
            allSlices: [],              // every warehouse's rules - tells which MSIL plants are SHARED
            stockYards: [],
            loadingPorts: [],           // warehouses + their MSIL plant codes, for the dropdown
            editorOpen: false,
            editorType: "slice",        // 'slice' | 'stock_yard'
            editorMode: "add",
            editorData: {},
            tagInput: {},               // what is being typed in each chip field, before Enter
            // MSIL child codes defined on the Transporters master, per load type:
            // { regular: [{code, transporter_name, transporter_code}], hub: [...], dedicated: [...] }
            childCodes: {},
            basicTitle: "",
            basicType: "default",
            show: false
        };
    }

    componentDidMount() {
        if (this.props.open) { this.loadConfig(); }
    }

    // Read on OPEN, not on mount, so the host screen pays nothing for a pop-up nobody opened, and
    // a re-open always shows what is stored now. A warehouse change while open reloads too.
    componentDidUpdate(prev) {
        if (this.props.open && !prev.open) { this.loadConfig(); }
        else if (this.props.open && prev.loadingPortCode !== this.props.loadingPortCode) { this.loadConfig(); }
    }

    readOnly() {
        return this.props.readOnly === true;
    }

    warehouse() {
        return this.props.loadingPortCode || "";
    }

    // The warehouse's own MSIL plant, read from the Loading Ports master EVERY TIME it is needed.
    // It used to be copied into state when the configuration loaded, so switching warehouse
    // re-rendered the name immediately while the plant still showed the previous warehouse's -
    // which is how "MSIL plant RPDC-BAN is credited to Kharkhoda" appeared on screen. Bangalore's
    // plant was never credited to Kharkhoda; only the sentence was wrong.
    warehousePlant() {
        var wh = this.warehouse();
        var hit = (this.state.loadingPorts || []).filter(function (p) { return p.loading_port_code === wh; });
        return hit.length > 0 ? String(hit[0].comp_short_name || "").trim() : "";
    }

    warehouseName() {
        var wh = this.warehouse();
        var hit = (this.state.loadingPorts || []).filter(function (p) { return p.loading_port_code === wh; });
        if (hit.length > 0 && hit[0].loading_port) { return hit[0].loading_port; }
        return warehouseLabel(wh);
    }

    loadConfig() {
        this.setState({ loadshow: 'show-m' });
        var cfgP = redirectURL.post("/lmconsignees/getActualSobConfig", {})
            .then((r) => (r.data && r.data.records) || [])
            .catch(() => null);
        // The warehouse's own MSIL plant code, so a new rule does not ask the user to remember it.
        var portP = redirectURL.post("/lmconsignees/getLmLoadingPorts", {})
            .then((r) => (Array.isArray(r.data) ? r.data : []))
            .catch(() => []);
        // The MSIL child codes each load type can offer, read from the Transporters master - one
        // call per tab, because a tab is where the list is maintained. Codes are no longer typed
        // here (management, 2026-10-15): free entry is what put them in the code in the first
        // place, and a code that exists nowhere in the master cannot be resolved to a transporter,
        // so the dispatch is dropped from actual SOB with nothing on screen to show for it.
        var codesP = Promise.all(LOAD_TYPES.map((t) => this.fetchChildCodes(t)))
            .then(function (lists) {
                var out = {};
                LOAD_TYPES.forEach(function (t, i) { out[t.value] = lists[i]; });
                return out;
            });
        return Promise.all([cfgP, portP, codesP]).then((res) => {
            var records = res[0];
            if (records === null) {
                this.setState({ loadshow: 'show-n', basicTitle: "Could not read the configuration",
                                basicType: "danger", show: true });
                return;
            }
            var wh = this.warehouse();
            var port = (res[1] || []).filter(function (p) { return p.loading_port_code === wh; });
            this.setState({
                allSlices: records.filter(function (x) { return x.config_type === "slice"; }),
                slices: records.filter(function (x) {
                    return x.config_type === "slice" && (!wh || x.loading_port_code === wh);
                }),
                // Stock yards are per warehouse too (client, 2026-10-14). A document saved before
                // that carries no warehouse; it is shown everywhere, since that is how it is used.
                stockYards: records.filter(function (x) {
                    return x.config_type === "stock_yard"
                        && (!wh || !x.loading_port_code || x.loading_port_code === wh);
                }),
                loadingPorts: (res[1] || []).filter(function (p) { return p.comp_short_name; }),
                childCodes: res[2] || {},
                loadshow: 'show-n'
            });
        });
    }

    // One load type's codes, flattened to one entry per CODE with the transporter it belongs to -
    // the user picks a code, but needs to see whose it is. A failure returns an empty list rather
    // than rejecting: the configuration itself must still open, and the field says it is empty.
    fetchChildCodes(loadType) {
        return redirectURL.post("/lmconsignees/getTransporterChildCodes", {
            loading_port_code: this.warehouse(),
            data_type: loadType.master.data_type,
            hub_transporter: loadType.master.hub_transporter,
            air_courier_transporter: 0
            // WAREHOUSE-SPECIFIC, and this tab only (client, 2026-10-15). A rule may therefore
            // need a code whose transporter is not on this warehouse's master yet - Manesar's
            // seeded rule claims GUR / GUR20, owned by a transporter with no Manesar row. That is
            // the right thing to surface: the transporter has to be added to this warehouse for
            // its volume to be credited here at all. The code stays visible on the rule, flagged
            // as not in the master, so nothing disappears while that is sorted out.
        }).then(function (r) {
            var records = (r.data && r.data.records) || [];
            var out = [];
            var seen = {};
            records.forEach(function (t) {
                (t.child_codes || []).forEach(function (code) {
                    if (code && seen[code] !== true) {
                        seen[code] = true;
                        out.push({ code: code, transporter_code: t.transporter_code,
                                   transporter_name: t.transporter_name });
                    }
                });
            });
            return out;
        }).catch(function () { return []; });
    }

    // What the codes field offers for the rule being edited: every code defined on the matching
    // Transporters tab, plus ALL. Codes ALREADY SAVED on this rule are added even when the master
    // no longer lists them - dropping them from the list would make them vanish from the chips on
    // the next save, silently narrowing the rule. They are labelled so the gap is visible.
    childCodeOptions(loadTypeValue, selected) {
        var list = ((this.state.childCodes || {})[loadTypeValue] || []).slice();
        list.sort(function (a, b) {
            var an = String(a.transporter_name || ""), bn = String(b.transporter_name || "");
            if (an !== bn) { return an < bn ? -1 : 1; }
            return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
        });
        var options = list.map(function (c) {
            return { value: c.code,
                     label: c.code + (c.transporter_name ? "  -  " + c.transporter_name : "") };
        });
        var known = {};
        options.forEach(function (o) { known[o.value] = true; });
        (selected || []).forEach(function (code) {
            if (code !== ALL_CODES && known[code] !== true) {
                known[code] = true;
                options.push({ value: code, label: code + "  -  not in the Transporters master" });
            }
        });
        options.unshift({ value: ALL_CODES, label: "ALL  -  every transporter of this plant" });
        return options;
    }

    // ---- add / edit through the SHARED master form (same look as every other master screen) ----

    openEditor(type, row) {
        var data;
        if (type === "slice") {
            data = row ? {
                slice_key: row.slice_key || "",
                consignor_code: row.consignor_code || "",
                load_type: loadTypeOf(row),
                stock_yard_rule: row.stock_yard_rule || "ignore",
                transporter_codes: (row.transporter_codes || []).slice()
            } : {
                slice_key: "",
                consignor_code: this.warehousePlant(),
                load_type: "regular", stock_yard_rule: "ignore", transporter_codes: []
            };
        } else {
            data = row ? { scope: row.scope || this.warehouse(), codes: (row.codes || []).slice() }
                       : { scope: this.warehouse(), codes: [] };
        }
        this.setState({ editorOpen: true, editorType: type, editorMode: row ? "update" : "add",
                        editorData: data, tagInput: {} });
    }

    // Field definitions for the pop-up form. `info` is rendered as an ⓘ beside the label, so an
    // explanation is available at the point of entry, not only on the grid header.
    editorFields() {
        var isNew = this.state.editorMode === "add";
        if (this.state.editorType === "slice") {
            return [
                { field: "slice_key", title: "Rule name", type: "text", required: true,
                  disabled: !isNew, placeholder: "GUR_HUB",
                  info: "Your own name for this rule - it only identifies it. It cannot be "
                      + "changed later; remove the rule and add it again instead." },
                { field: "consignor_code", title: "MSIL plant", type: "select", required: true,
                  options: this.plantOptions(),
                  info: "The despatching MSIL plant, as the feed reports it. Only this warehouse's "
                      + "own plant and plants that despatch for several warehouses are offered - "
                      + "another warehouse's own plant can never send a load here. For Gurgaon hub, "
                      + "RPDC Gurgaon and DELHI NCR the plant is the Gurgaon one, because all three "
                      + "despatch under it." },
                { field: "load_type", title: "Load type", type: "select", options: LOAD_TYPES,
                  info: "Which share of business the volume counts against - the zone's (Regular), "
                      + "the hub's own (Hub), or the dedicated one (Dedicated)." },
                { field: "stock_yard_rule", title: "Stock yard dispatches", type: "select",
                  options: STOCK_YARD_RULES,
                  info: "A stock yard dispatch goes to an MSIL yard instead of a dealer. Choose "
                      + "whether this rule takes only those, leaves them out, or counts both. The "
                      + "codes that mark a stock yard are listed below the rules." },
                { field: "transporter_codes", title: "Transporter child codes", type: "codes",
                  required: true, placeholder: "Select the MSIL child codes",
                  info: "MSIL's CHILD transporter codes, picked from the Transporters master - the "
                      + "list follows the load type above (Hub codes from the Hub tab, and so on). "
                      + "A dispatch carrying one of them is credited to this warehouse. A code that "
                      + "is not offered has to be added against its transporter on the Transporters "
                      + "master first; one that exists nowhere there cannot be resolved to a "
                      + "transporter and its volume is dropped. Pick ALL to claim every transporter "
                      + "of this plant - only where the plant despatches for this warehouse alone." }
            ];
        }
        // No name field: a warehouse has ONE stock-yard list, so the document is keyed by the
        // warehouse itself (stamped in saveEditor). Nothing to name, nothing to get wrong.
        return [
            { field: "codes", title: "Stock yard consignee codes", type: "tags", required: true,
              placeholder: "Type a consignee code and press Enter",
              info: "Consignees that are MSIL stock yards rather than dealers. The rules above "
                  + "use this list through their Stock yard dispatches setting." }
        ];
    }

    // MSIL plants this warehouse could plausibly be credited from. Listing all nine invited the
    // wrong pick - Siliguri was offered Gujarat's and Bangalore's plants, which can never despatch
    // for it. Two kinds are offered, and nothing else:
    //   - the warehouse's OWN plant (the normal case), and
    //   - plants that already despatch for more than one warehouse (today MSILG and MSILDM) -
    //     which is exactly the situation a rule exists for, and is read from the rules themselves
    //     rather than hardcoded, so a new shared plant appears here the moment it is configured.
    plantOptions() {
        var self = this;
        var own = this.warehousePlant();
        var names = {};
        (this.state.loadingPorts || []).forEach(function (p) {
            var code = String(p.comp_short_name || "").trim();
            if (code) { names[code] = p.loading_port || code; }
        });
        var out = [];
        var seen = {};
        var add = function (code, suffix) {
            if (!code || seen[code]) { return; }
            seen[code] = true;
            out.push({ value: code, label: (names[code] || code) + " (" + code + ")" + suffix });
        };
        add(own, " - this warehouse");
        (this.state.allSlices || []).forEach(function (sl) {
            var code = String(sl.consignor_code || "").trim();
            // Only a plant that serves a warehouse OTHER than its own is worth offering here.
            if (code && code !== own && names[code] !== undefined
                    && sl.loading_port_code !== self.warehouseOfPlant(code)) {
                add(code, " - despatches for several warehouses");
            }
        });
        return out;
    }

    // The warehouse a plant belongs to on the Loading Ports master (its "home" warehouse).
    warehouseOfPlant(code) {
        var hit = (this.state.loadingPorts || []).filter(function (p) { return p.comp_short_name === code; });
        return hit.length > 0 ? hit[0].loading_port_code : "";
    }

    setTagInput(field, text) {
        var t = Object.assign({}, this.state.tagInput);
        t[field] = text;
        this.setState({ tagInput: t });
    }

    // Turn whatever is typed into chips. Returns true when it consumed the keystroke, so Enter
    // does not also submit or close anything while the user is still adding codes.
    commitTag(field) {
        var text = this.state.tagInput[field] || "";
        if (String(text).trim() === "") { return false; }
        var current = Array.isArray(this.state.editorData[field]) ? this.state.editorData[field] : [];
        this.setEditorField(field, mergeCodes(current, text));
        this.setTagInput(field, "");
        return true;
    }

    setEditorField(field, value) {
        var data = Object.assign({}, this.state.editorData);
        data[field] = value;
        // The codes offered come from the load type's own Transporters tab, so a code picked under
        // Regular is not necessarily defined under Hub. Anything the new load type does not offer
        // is dropped here, while the user can still see it happen - leaving it would save a rule
        // against codes that tab never defined, which is the situation this screen exists to end.
        if (field === "load_type" && Array.isArray(data.transporter_codes)) {
            var offered = {};
            this.childCodeOptions(value, []).forEach(function (o) { offered[o.value] = true; });
            data.transporter_codes = data.transporter_codes.filter(function (c) {
                return offered[c] === true;
            });
        }
        this.setState({ editorData: data });
    }

    saveEditor() {
        var type = this.state.editorType;
        var data = this.state.editorData;
        var missing = this.editorFields().filter(function (f) {
            var v = data[f.field];
            var empty = Array.isArray(v) ? v.length === 0
                                         : String(v == undefined ? "" : v).trim() === "";
            return f.required && empty;
        });
        if (missing.length > 0) {
            this.setState({ basicTitle: "Please enter " + missing[0].title.toLowerCase(),
                            basicType: "warning", show: true });
            return;
        }
        var payload = Object.assign({ config_type: type }, data, {
            email: localStorage.getItem("email") || "",
            user_name: localStorage.getItem("username") || "",
            staff_id: localStorage.getItem("staff_id") || "",
            comp_code: localStorage.getItem("comp_code") || "",
            user_department: localStorage.getItem("lm_department") || "",
            user_role: localStorage.getItem("roles") || "",
            designation: localStorage.getItem("designation") || ""
        });
        // Both kinds belong to the warehouse the screen is on. The Optirun warehouse CODE is
        // stored but never shown or asked for - the user works in warehouse names.
        payload.loading_port_code = this.warehouse();
        if (type === "stock_yard") { payload.scope = this.warehouse(); }
        if (type === "slice") {
            // Load type is ONE choice for the user; the data keeps a hub flag and a planning type.
            var lt = LOAD_TYPES.filter(function (t) { return t.value === data.load_type; });
            var chosen = lt.length > 0 ? lt[0] : LOAD_TYPES[0];
            payload.is_hub = chosen.is_hub;
            payload.data_type = chosen.data_type;
            delete payload.load_type;
        }
        this.setState({ loadshow: 'show-m' });
        redirectURL.post("/lmconsignees/saveActualSobConfig", payload)
            .then((r) => {
                if (r.data && r.data.status === "success") {
                    this.setState({ editorOpen: false, basicTitle: "Saved", basicType: "success", show: true });
                    var self = this;
                    this.loadConfig().then(function () { self.promptForStockYards(data, type); });
                } else {
                    this.setState({ loadshow: 'show-n',
                                    basicTitle: (r.data && r.data.message) || "Could not save",
                                    basicType: "warning", show: true });
                }
            })
            .catch((e) => {
                this.setState({ loadshow: 'show-n', basicTitle: "Could not save: " + e.message,
                                basicType: "danger", show: true });
            });
    }

    // A rule that EXCLUDES or takes ONLY stock yard dispatches cannot do either until the stock
    // yard codes exist - with an empty list it silently claims everything, or nothing. So the
    // stock yard form is opened straight after saving such a rule, once, and only while the list
    // is still empty: a warehouse that already has codes is never interrupted.
    promptForStockYards(saved, type) {
        if (type !== "slice") { return; }
        var rule = saved.stock_yard_rule;
        if (rule !== "exclude" && rule !== "include") { return; }
        var listed = (this.state.stockYards || []).reduce(function (n, d) {
            return n + ((d.codes || []).length);
        }, 0);
        if (listed > 0) { return; }
        this.setState({
            basicTitle: "This rule uses stock yards, but none are listed for "
                        + this.warehouseName() + " yet. Add them to make it work.",
            basicType: "warning", show: true
        });
        this.openEditor("stock_yard", null);
    }

    removeConfig(type, row) {
        var key = { config_type: type, email: localStorage.getItem("email") || "" };
        if (type === "slice") { key.slice_key = row.slice_key; }
        else { key.scope = row.scope; }
        this.setState({ loadshow: 'show-m' });
        redirectURL.post("/lmconsignees/deleteActualSobConfig", key)
            .then(() => { this.loadConfig(); })
            .catch(() => { this.setState({ loadshow: 'show-n' }); });
    }

    actionCell(type) {
        var self = this;
        var readOnly = this.readOnly();
        return function (params) {
            if (readOnly) { return ""; }
            return (
                <span>
                    <i className="icofont icofont-edit" title="Edit"
                       style={{ cursor: "pointer", color: "#1976d2", marginRight: "12px" }}
                       onClick={() => self.openEditor(type, params.data)}></i>
                    <i className="icofont icofont-trash" title="Remove"
                       style={{ cursor: "pointer", color: "#c62828" }}
                       onClick={() => self.removeConfig(type, params.data)}></i>
                </span>
            );
        };
    }

    renderRules() {
        // No "Order" column (client, 2026-10-14): rules do not compete. Each one selects its own
        // dispatches, so the sequence they are applied in cannot change the outcome - showing an
        // order would suggest a decision the user does not have to make.
        var cols = [
            { headerName: "Rule Name" + INFO, field: "slice_key", width: 130,
              headerTooltip: "Your name for this rule. It only identifies the rule." },
            { headerName: "MSIL Plant Code" + INFO, field: "consignor_code", width: 150,
              headerTooltip: "The despatching MSIL plant as the feed reports it (the consignor code)." },
            { headerName: "Load Type" + INFO, field: "is_hub", width: 110,
              valueGetter: (p) => loadTypeLabel(p.data),
              headerTooltip: "Hub dispatches are credited against the hub's own share of business, "
                           + "regular ones against the zone's." },
            { headerName: "Stock Yard Dispatches" + INFO, field: "stock_yard_rule", width: 210,
              valueGetter: (p) => ruleLabel(p.data.stock_yard_rule),
              headerTooltip: "A stock yard dispatch goes to an MSIL yard instead of a dealer. "
                           + "This says whether the rule takes those, leaves them out, or counts both." },
            // No separate count column: the codes themselves are in the next column, so a number
            // beside them only invited the reading that it meant something else (a plant code).
            { headerName: "Transporter Child Codes" + INFO, field: "transporter_codes", flex: 1, minWidth: 280,
              valueGetter: (p) => ((p.data.transporter_codes || [])[0] === ALL_CODES)
                                    ? "All transporters of this plant"
                                    : codesToText(p.data.transporter_codes),
              tooltipValueGetter: (p) => codesToText(p.data.transporter_codes),
              headerTooltip: "MSIL's CHILD codes. A dispatch carrying one of these is credited to "
                           + "this warehouse; the transporter is resolved from the Transporters master." },
            { headerName: "", width: 90, cellRendererFramework: this.actionCell("slice") }
        ];
        var plant = this.warehousePlant();
        // A warehouse with no rules is NOT unconfigured - it is credited through its own MSIL
        // plant, which the Loading Ports master already records. Say that plainly, so an empty
        // grid does not read as missing setup.
        var empty = plant
            ? "Nothing to set up here. " + this.warehouseName() + " is credited with everything its "
              + "own MSIL plant (" + plant + ") despatches. Rules are needed only when ONE plant "
              + "despatches for SEVERAL warehouses - as the Gurgaon plant does for Gurgaon, RPDC "
              + "Gurgaon and DELHI NCR."
            : "This warehouse has no MSIL plant code on the Loading Ports master, so its dispatches "
              + "cannot be credited. Set it there first.";
        return this.section(
            "Transporter codes credited to " + this.warehouseName(),
            "Needed only where several warehouses despatch under ONE MSIL plant code - then the "
            + "transporter code is what says which of them earned the volume.",
            cols, this.state.slices, "slice", empty);
    }

    renderStockYards() {
        var cols = [
            { headerName: "Codes" + INFO, width: 85,
              valueGetter: (p) => (p.data.codes || []).length,
              headerTooltip: "How many consignee codes are listed." },
            { headerName: "Stock Yard Consignee Codes" + INFO, field: "codes", flex: 1,
              valueGetter: (p) => codesToText(p.data.codes),
              tooltipValueGetter: (p) => codesToText(p.data.codes),
              headerTooltip: "Consignees that are MSIL stock yards rather than dealers." },
            { headerName: "", width: 90, cellRendererFramework: this.actionCell("stock_yard") }
        ];
        return this.section(
            "Stock yards",
            "The consignee codes that make a dispatch a stock yard movement, for " + this.warehouseName()
            + ". The rules above use this list through their Stock Yard Dispatches setting.",
            cols, this.state.stockYards, "stock_yard",
            "No stock yards listed for " + this.warehouseName()
            + " - every dispatch of this warehouse counts as a dealer delivery.");
    }

    section(title, help, cols, rows, type, emptyText) {
        return (
            <div className="card" style={{ borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "14px" }}>
                <div className="d-flex justify-content-between align-items-center"
                     style={{ flexWrap: "wrap", gap: "10px", padding: "12px 18px" }}>
                    <div>
                        <h5 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#1a237e" }}>{title}</h5>
                        <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px", maxWidth: "900px" }}>{help}</div>
                    </div>
                    {this.readOnly() ? "" :
                        <button type="button" className="pill-btn" style={{ backgroundColor: "#f57c00", color: "#fff" }}
                                onClick={() => this.openEditor(type, null)}>Add</button>}
                </div>
                {/* The empty message is rendered HERE, not as ag-Grid's overlayNoRowsTemplate:
                    the grid captures that template when it mounts and keeps showing it, so after
                    a warehouse switch it still read out the previous warehouse's plant. */}
                {(rows || []).length === 0 ?
                    <div style={{ padding: "0 18px 18px 18px", fontSize: "12px", color: "#64748b",
                                  lineHeight: "18px" }}>{emptyText}</div>
                    :
                    <div className="ag-theme-balham" style={{ height: "230px", width: "100%", padding: "0 18px 18px 18px" }}>
                        <AgGridReact modules={AllModules} columnDefs={cols} rowData={rows}
                                     defaultColDef={GRID_DEFAULTS} rowHeight={32} headerHeight={36}
                                     enableBrowserTooltips={true} />
                    </div>}
            </div>
        );
    }

    // ADD / EDIT as a POP-UP, not a slider (client, 2026-10-14): the configuration itself opens
    // as a dialog, so a form that slid in from the side read as a different screen. Same label /
    // required-marker / help-note shape the master forms use, with an ⓘ per field.
    renderEditor() {
        if (!this.state.editorOpen) { return null; }
        var self = this;
        var fields = this.editorFields();
        var data = this.state.editorData;
        var inputStyle = { padding: "8px", borderRadius: "6px", border: "1px solid #ccc",
                           width: "100%", fontSize: "13px" };
        var title = (this.state.editorMode === "add" ? "Add " : "Edit ")
                  + (this.state.editorType === "slice" ? "transporter code rule" : "stock yards");
        return (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)",
                          zIndex: 1210, display: "flex", alignItems: "center", justifyContent: "center" }}
                 onClick={() => this.setState({ editorOpen: false })}>
                <div style={{ background: "#fff", borderRadius: "10px", width: "min(560px, 94vw)",
                              maxHeight: "88vh", display: "flex", flexDirection: "column" }}
                     onClick={(e) => e.stopPropagation()}>
                    <div className="slider-header" style={{ borderRadius: "10px 10px 0 0" }}>
                        {title}
                        <span style={{ cursor: "pointer" }}
                              onClick={() => this.setState({ editorOpen: false })}>✕</span>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px" }}>
                        {fields.map(function (f) {
                            var value = data[f.field] == undefined ? "" : data[f.field];
                            return (
                                <div key={f.field} className="d-flex flex-column" style={{ marginBottom: "12px" }}>
                                    <label className="m-0" style={{ fontSize: "13px" }}>
                                        {f.title} {f.required ? <span className="text-danger">*</span> : ""}
                                        {f.info ?
                                            <i className="icofont icofont-info-circle" title={f.info}
                                               style={{ marginLeft: "6px", color: "#1976d2", cursor: "help" }}></i>
                                            : ""}
                                    </label>
                                    {f.type === "codes" ?
                                        // DROPDOWN, not free text (management, 2026-10-15). The
                                        // codes are defined once against the LSP on the
                                        // Transporters master and only picked here, so a rule can
                                        // never name a code the system cannot resolve. The list
                                        // follows the load type, which is why it is read here and
                                        // not captured when the form opened.
                                        <Select
                                            isMulti
                                            className="mt-2 mb-1"
                                            classNamePrefix="react-select"
                                            placeholder={f.placeholder || ""}
                                            menuPlacement="auto"
                                            options={self.childCodeOptions(data.load_type,
                                                                           Array.isArray(value) ? value : [])}
                                            noOptionsMessage={() => "No MSIL child codes are defined for this load type yet - add them on the Transporters master"}
                                            value={(Array.isArray(value) ? value : []).map(function (c) {
                                                return { value: c, label: c === ALL_CODES ? "ALL" : c };
                                            })}
                                            onChange={(picked) => {
                                                var codes = (picked || []).map(function (o) { return o.value; });
                                                // ALL means the whole plant. Mixed with a list it
                                                // would read as "all of them, and also these" and
                                                // mean nothing, so it stands alone - the server
                                                // enforces the same thing.
                                                if (codes.indexOf(ALL_CODES) >= 0) { codes = [ALL_CODES]; }
                                                self.setEditorField(f.field, codes);
                                            }} />
                                        : f.type === "tags" ?
                                        // Codes as removable chips, not a block of comma separated
                                        // text: a 30-code list cannot be proof-read as one string,
                                        // and a typo buried in it fails silently.
                                        // The menu is suppressed (there is nothing to pick from),
                                        // and with no menu react-select has no "Create ..." option
                                        // for Enter to commit - so the typed text is held here and
                                        // committed on Enter / Tab / comma, which is the documented
                                        // way to build a tag input out of this component.
                                        <CreatableSelect
                                            isMulti
                                            className="mt-2 mb-1"
                                            classNamePrefix="react-select"
                                            placeholder={f.placeholder || ""}
                                            menuIsOpen={false}
                                            isClearable={false}
                                            components={{ DropdownIndicator: null }}
                                            inputValue={self.state.tagInput[f.field] || ""}
                                            onInputChange={(text) => self.setTagInput(f.field, text)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
                                                    if (self.commitTag(f.field)) { e.preventDefault(); }
                                                }
                                            }}
                                            onBlur={() => self.commitTag(f.field)}
                                            value={(Array.isArray(value) ? value : []).map(function (c) {
                                                return { value: c, label: c };
                                            })}
                                            onChange={(picked) => self.setEditorField(f.field,
                                                (picked || []).map(function (o) { return o.value; }))}
                                            onPaste={(e) => {
                                                var text = (e.clipboardData || window.clipboardData).getData("text");
                                                if (!text) { return; }
                                                e.preventDefault();
                                                var current = Array.isArray(data[f.field]) ? data[f.field] : [];
                                                self.setEditorField(f.field, mergeCodes(current, text));
                                                self.setTagInput(f.field, "");
                                            }} />
                                        : f.type === "select" ?
                                        <select className="mt-2 mb-1" style={inputStyle} value={value}
                                                onChange={(e) => self.setEditorField(f.field,
                                                    f.numeric ? parseInt(e.target.value) : e.target.value)}>
                                            <option value="">Select</option>
                                            {(f.options || []).map(function (o) {
                                                return <option key={o.value} value={o.value}>{o.label}</option>;
                                            })}
                                        </select>
                                        : f.type === "textarea" ?
                                        <textarea className="mt-2 mb-1" rows={f.rows || 6} value={value}
                                                  placeholder={f.placeholder || ""}
                                                  style={inputStyle}
                                                  onChange={(e) => self.setEditorField(f.field, e.target.value)} />
                                        :
                                        <input type="text" className="mt-2 mb-1" value={value}
                                               placeholder={f.placeholder || ""} disabled={!!f.disabled}
                                               style={Object.assign({}, inputStyle,
                                                   f.disabled ? { background: "#e9ecef", cursor: "not-allowed" } : {})}
                                               onChange={(e) => self.setEditorField(f.field, e.target.value)} />}
                                    {f.info ?
                                        <small className="text-muted" style={{ fontSize: "11px" }}>{f.info}</small>
                                        : ""}
                                </div>
                            );
                        })}
                    </div>
                    {/* NEITHER button carries a class. `.save-btn` and the pill classes each bring
                        their own padding, font-size and line-height, so the two never matched no
                        matter what was added on top - one style object, differing only in colour,
                        is the only way they stay the same size. Fixed height + inline-flex centres
                        the label, so a longer word cannot make one of them taller. */}
                    <div className="slider-footer" style={{ borderRadius: "0 0 10px 10px", gap: "8px",
                                                            alignItems: "center" }}>
                        {[{ key: "cancel", label: "Cancel", bg: "#e2e8f0", fg: "#334e68",
                            onClick: () => this.setState({ editorOpen: false }) },
                          { key: "save", label: this.state.editorMode === "add" ? "Save" : "Update",
                            bg: "#28a745", fg: "#fff", onClick: () => this.saveEditor() }
                        ].map(function (b) {
                            return (
                                <button type="button" key={b.key} onClick={b.onClick}
                                        style={{ background: b.bg, color: b.fg, border: "none",
                                                 height: "34px", minWidth: "96px", padding: "0 16px",
                                                 fontSize: "13px", fontWeight: 600, lineHeight: "34px",
                                                 borderRadius: "6px", cursor: "pointer", margin: 0,
                                                 display: "inline-flex", alignItems: "center",
                                                 justifyContent: "center", boxSizing: "border-box" }}>
                                    {b.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.props.open) { return null; }
        return (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)",
                          zIndex: 1190, display: "flex", alignItems: "center", justifyContent: "center" }}
                 onClick={() => { if (this.props.onClose) { this.props.onClose(); } }}>
                <div style={{ background: "#f7f9fc", borderRadius: "10px", width: "min(1180px, 95vw)",
                              height: "min(820px, 92vh)", display: "flex", flexDirection: "column",
                              padding: "14px 16px" }}
                     onClick={(e) => e.stopPropagation()}>
                    <SweetAlert show={this.state.show} type={this.state.basicType} title={this.state.basicTitle}
                                onConfirm={() => this.setState({ show: false })} />
                    <div className="d-flex align-items-center" style={{ gap: "14px", paddingBottom: "10px" }}>
                        <div style={{ width: "5px", height: "26px", background: "linear-gradient(180deg,#1976d2,#42a5f5)",
                                      borderRadius: "3px", flexShrink: 0 }}></div>
                        <div>
                            <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#1a237e" }}>
                                Actual SOB Configuration
                                <span style={{ fontSize: "12px", fontWeight: 600, color: "#1565c0",
                                               background: "#eef4fb", border: "1px solid #bcd6f0",
                                               borderRadius: "11px", padding: "1px 10px", marginLeft: "10px" }}>
                                    {this.warehouseName() || "No warehouse selected"}
                                    {this.warehousePlant() ? " - MSIL plant " + this.warehousePlant() : ""}
                                </span>
                            </h5>
                            <div style={{ fontSize: "11.5px", color: "#64748b" }}>
                                How MSIL dispatches are credited to this warehouse's share of business.
                                {this.readOnly() ? " You have view-only access to this screen." : ""}
                            </div>
                        </div>
                        <button type="button" className="pill-btn pill-btn-info pill-btn-soft" style={{ marginLeft: "auto" }}
                                onClick={() => this.loadConfig()}>Refresh</button>
                        <span style={{ cursor: "pointer", color: "#607d8b", fontSize: "16px" }}
                              onClick={() => { if (this.props.onClose) { this.props.onClose(); } }}>✕</span>
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: "2px" }}>
                        {this.renderRules()}
                        {this.renderStockYards()}
                    </div>

                    {this.renderEditor()}

                    <div className={"dataLoadpage " + this.state.loadshow}
                         style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1195 }}></div>
                    <div className={"dataLoadpageimg " + this.state.loadshow}
                         style={{ position: "fixed", top: "40%", left: "45%", zIndex: 1196 }}>
                        <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
                    </div>
                </div>
            </div>
        );
    }
}
